import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Smartphone, Radio, RefreshCw, Save, Copy, Eye, EyeOff,
  Loader2, MapPin, Info, KeyRound, Truck,
} from "lucide-react";
import TataFleetSection from "@/pages/admin/tracking/TataFleetSection";

/**
 * Live tracking configuration.
 *
 * Global default source (driver_app vs gps_device) + per-bowser override + per-bowser
 * API key management for external GPS trackers.
 */
export default function TrackingSettings({ settings, onChange, onSave, saving }) {
  const source = settings?.tracking_source_default || "driver_app";
  const staleMin = settings?.tracking_stale_after_min ?? 15;
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="space-y-4" data-testid="tracking-settings">
      {/* Mixed-fleet reality: each bowser can use a different source.
          This top card shows a fleet-wide summary + sets the *fallback* for
          bowsers that haven't been overridden per-vehicle below. */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Tracking sources</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Mixed fleets are supported — each bowser can use a different source
            (Driver app / GPS device / Tata Fleet Edge). Pick per-bowser in the
            table below. The card you tap here becomes the <b>fallback</b> for
            any bowser you haven&apos;t overridden yet.
          </p>

          <SourceSummary />

          <div>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Fallback source (used when a bowser has no per-vehicle override)
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <SourceCard
                icon={Smartphone}
                title="Driver mobile app"
                subtitle="Uses the driver's phone GPS pings from the FuelExpress driver app."
                pros={["No hardware needed", "Free forever", "Position tied to driver check-in"]}
                cons={["Requires driver to keep the app open", "Depends on phone signal"]}
                selected={source === "driver_app"}
                onSelect={() => onChange({ tracking_source_default: "driver_app" })}
                testId="tracking-source-driver-app"
              />
              <SourceCard
                icon={Radio}
                title="Generic GPS device"
                subtitle="External vehicle-mounted GPS tracker pushes to our API. Works with any brand — Ashok Leyland, Eicher, Mahindra, aftermarket devices."
                pros={["Independent of driver phone", "Continuous 24×7 pings", "Vendor-agnostic push API"]}
                cons={["Hardware cost per bowser", "Device must be configured with API key"]}
                selected={source === "gps_device"}
                onSelect={() => onChange({ tracking_source_default: "gps_device" })}
                testId="tracking-source-gps-device"
              />
              <SourceCard
                icon={Truck}
                title="Tata Fleet Edge"
                subtitle="Pull GPS directly from OBD-II units on Tata bowsers via Fleet Edge OAuth API."
                pros={["No hardware install — factory-fitted", "Server-to-server, no phone dependency", "Fuel/DPF telemetry (roadmap)"]}
                cons={["Only Tata vehicles enrolled on Fleet Edge", "Requires OAuth credentials from Tata"]}
                selected={source === "tata_fleet"}
                onSelect={() => onChange({ tracking_source_default: "tata_fleet" })}
                testId="tracking-source-tata-fleet"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Mark position as offline after (minutes)</Label>
              <Input
                type="number"
                min="1"
                max="1440"
                value={staleMin}
                onChange={(e) => onChange({ tracking_stale_after_min: Number(e.target.value) || 15 })}
                data-testid="tracking-stale-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={onSave} disabled={saving} data-testid="tracking-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save fallback
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-bowser + API keys */}
      <BowserOverrideList defaultSource={source} apiUrl={apiUrl} />

      {/* Tata Fleet Edge OAuth + VIN enrollment — always visible so admins can
          enroll individual Tata bowsers even when the global default is
          driver_app or gps_device (mixed-fleet reality). */}
      <TataFleetSection />
    </div>
  );
}

/* ─────────────────── Fleet-wide source summary ─────────────────── */
function SourceSummary() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const { data } = useQuery({ queryKey: ["fleet"], queryFn: () => api.get("/fleet").then((r) => r.data) });
  const bowsers = data?.items || [];
  const fallback = settings?.tracking_source_default || "driver_app";

  // Compute effective source per bowser (override OR fallback).
  const counts = { driver_app: 0, gps_device: 0, tata_fleet: 0 };
  for (const b of bowsers) {
    const eff = (b.tracking_source || "").trim() || fallback;
    if (eff in counts) counts[eff] += 1;
  }
  const total = bowsers.length;

  if (total === 0) return null;

  const items = [
    { key: "driver_app", label: "Driver app", icon: Smartphone, color: "bg-blue-500" },
    { key: "gps_device", label: "GPS device", icon: Radio, color: "bg-amber-500" },
    { key: "tata_fleet", label: "Tata Fleet", icon: Truck, color: "bg-emerald-500" },
  ];

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-3" data-testid="tracking-source-summary">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Current mix ({total} bowser{total > 1 ? "s" : ""})</div>
      </div>
      {/* Segmented stacked bar */}
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {items.map((it) => {
          const pct = total ? (counts[it.key] / total) * 100 : 0;
          if (pct === 0) return null;
          return <div key={it.key} className={it.color} style={{ width: `${pct}%` }} title={`${it.label}: ${counts[it.key]}`} />;
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-1.5" data-testid={`source-count-${it.key}`}>
            <span className={`h-2 w-2 rounded-full ${it.color}`} />
            <it.icon className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">{it.label}</span>
            <span className="font-mono font-semibold">{counts[it.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── Source picker card ─────────────────── */
function SourceCard({ icon: Icon, title, subtitle, pros, cons, selected, onSelect, testId }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testId}
      className={`group flex flex-col items-start rounded-lg border-2 p-4 text-left transition-all ${
        selected
          ? "border-primary bg-primary/5 shadow-md"
          : "border-border bg-background hover:border-primary/40 hover:bg-primary/[0.02]"
      }`}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`rounded-md p-2 ${selected ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="font-semibold">{title}</div>
        </div>
        {selected && <Badge variant="default" className="text-[10px]">Fallback</Badge>}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{subtitle}</p>
      <ul className="mt-3 space-y-1 text-xs">
        {pros.map((p) => (
          <li key={p} className="flex items-start gap-1.5">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-green-500" /> {p}
          </li>
        ))}
        {cons.map((c) => (
          <li key={c} className="flex items-start gap-1.5 text-muted-foreground">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-orange-500" /> {c}
          </li>
        ))}
      </ul>
    </button>
  );
}

/* ─────────────────── Per-bowser overrides ─────────────────── */
function BowserOverrideList({ defaultSource, apiUrl }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["fleet"],
    queryFn: () => api.get("/fleet/bowsers").then((r) => r.data),
  });
  const bowsers = data?.items || [];

  const setSource = useMutation({
    mutationFn: ({ id, source }) => api.patch(`/tracking/bowsers/${id}/source`, { source }),
    onSuccess: () => { toast.success("Source updated"); qc.invalidateQueries({ queryKey: ["fleet"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const rotateKey = useMutation({
    mutationFn: (id) => api.post(`/tracking/gps-device/keys/${id}/rotate`).then((r) => r.data),
    onSuccess: (res) => {
      toast.success("API key generated");
      qc.invalidateQueries({ queryKey: ["fleet"] });
      // Show a one-time reveal
      window.__lastRotatedKey = res.api_key;
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Rotate failed"),
  });

  const revokeKey = useMutation({
    mutationFn: (id) => api.post(`/tracking/gps-device/keys/${id}/revoke`),
    onSuccess: () => { toast.success("API key revoked"); qc.invalidateQueries({ queryKey: ["fleet"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold">
              <KeyRound className="h-5 w-5 text-primary" /> Per-bowser configuration
            </div>
            <p className="text-xs text-muted-foreground">
              Override the source for individual bowsers, or issue API keys for external GPS trackers.
            </p>
          </div>
        </div>

        <Alert className="bg-primary/5 border-primary/20">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs space-y-1">
            <div><strong>GPS device integration endpoint:</strong></div>
            <code className="block break-all rounded bg-muted p-1.5 font-mono text-[11px]">
              POST {apiUrl}/api/tracking/gps-device
            </code>
            <div>Headers: <code className="text-[11px]">X-API-Key: &lt;bowser api key&gt;</code></div>
            <div>Body JSON: <code className="text-[11px]">{`{ "registration_no": "MH12AB1234", "lat": 18.52, "lng": 73.85, "speed_kmph": 40, "heading": 90 }`}</code></div>
          </AlertDescription>
        </Alert>

        {isLoading && <div className="text-xs text-muted-foreground">Loading bowsers…</div>}

        {!isLoading && bowsers.length === 0 && (
          <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
            No bowsers found. Add bowsers from the Fleet module first.
          </div>
        )}

        {bowsers.length > 0 && (
          <div className="space-y-2">
            {bowsers.map((b) => (
              <BowserRow
                key={b.id}
                bowser={b}
                defaultSource={defaultSource}
                onSetSource={(source) => setSource.mutate({ id: b.id, source })}
                onRotate={() => rotateKey.mutate(b.id)}
                onRevoke={() => revokeKey.mutate(b.id)}
                rotating={rotateKey.isPending && rotateKey.variables === b.id}
                lastRotatedKey={window.__lastRotatedKey}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BowserRow({ bowser: b, defaultSource, onSetSource, onRotate, onRevoke, rotating, lastRotatedKey }) {
  const [reveal, setReveal] = useState(false);
  const effectiveSource = (b.tracking_source || "").trim() || defaultSource;
  const hasKey = !!b.gps_device_api_key;

  const copy = (val) => {
    navigator.clipboard?.writeText?.(val)
      ?.then(() => toast.success("Copied"))
      ?.catch(() => toast.error("Copy failed"));
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold">
            {b.code} · {b.registration_no}
            <Badge variant="outline" className="text-[10px]">
              {effectiveSource === "driver_app" ? (<><Smartphone className="mr-1 h-3 w-3" /> driver app</>)
                : effectiveSource === "tata_fleet" ? (<><Truck className="mr-1 h-3 w-3" /> Tata Fleet Edge</>)
                : (<><Radio className="mr-1 h-3 w-3" /> GPS device</>)}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            {b.assigned_driver_name ? `Assigned to ${b.assigned_driver_name}` : "No driver assigned"}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select
            value={b.tracking_source || "__default__"}
            onValueChange={(v) => onSetSource(v === "__default__" ? "" : v)}
          >
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid={`bowser-source-${b.code}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">Inherit default</SelectItem>
              <SelectItem value="driver_app">Driver app</SelectItem>
              <SelectItem value="gps_device">GPS device</SelectItem>
              <SelectItem value="tata_fleet">Tata Fleet Edge</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {effectiveSource === "gps_device" && (
        <div className="rounded-md border border-dashed bg-muted/30 p-2.5 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 font-semibold text-muted-foreground">
                <KeyRound className="h-3 w-3" /> Device API key
              </div>
              {hasKey ? (
                <div className="mt-1 flex items-center gap-1">
                  <code className="min-w-0 truncate rounded bg-background px-1.5 py-0.5 font-mono text-[10px]">
                    {reveal ? b.gps_device_api_key : `${"•".repeat(24)}${b.gps_device_api_key.slice(-4)}`}
                  </code>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setReveal(!reveal)} data-testid={`toggle-key-${b.code}`}>
                    {reveal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(b.gps_device_api_key)} data-testid={`copy-key-${b.code}`}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="mt-1 italic text-muted-foreground">No key yet — click Generate.</div>
              )}
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={onRotate}
                disabled={rotating}
                data-testid={`rotate-key-${b.code}`}
              >
                {rotating ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1.5 h-3 w-3" />}
                {hasKey ? "Rotate" : "Generate"}
              </Button>
              {hasKey && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={onRevoke}
                  data-testid={`revoke-key-${b.code}`}
                >
                  Revoke
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
